import { useState } from 'react';
import { format } from 'date-fns';
import { normalizeTimestampDate } from '../../utils/date';

export const trajectoryColors: Record<string, string> = {
  red: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  blue: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  magenta: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-200',
  green: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  yellow:
    'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
  orange:
    'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200',
  default: 'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-200',
};

interface LogEntryProps {
  timestamp: number | string;
  trajectoryName: string;
  description: string;
  season: string;
  image?: string;
  tags: string[];
  latitude?: number;
  longitude?: number;
}

export default function DeploymentLogEntry({
  timestamp,
  trajectoryName,
  description,
  season,
  image,
  tags,
  latitude,
  longitude,
}: LogEntryProps) {
  const [isImageOpen, setIsImageOpen] = useState(false);

  const dateObj = normalizeTimestampDate(timestamp);
  const formattedDate = format(dateObj, 'PPPp');

  const imagePath = image ? image : undefined;

  const coordinates =
    latitude && longitude
      ? `GPS: ${latitude.toFixed(5)}, ${longitude.toFixed(5)}`
      : null;

  return (
    <>
      <div className="bg-white dark:bg-gray-900 shadow-md rounded-lg p-4 transition-colors w-full flex flex-col sm:flex-row gap-4">
        <div className="flex-1 flex flex-col">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-2 gap-2">
            <span
              className={`px-2 py-1 rounded-full font-semibold text-xs ${
                trajectoryColors[trajectoryName.toLowerCase()] ||
                trajectoryColors.default
              }`}
            >
              {trajectoryName.charAt(0).toUpperCase() + trajectoryName.slice(1)}
            </span>

            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
              <span className="font-bold">
                {season.charAt(0).toUpperCase() + season.slice(1)}
              </span>
            </div>
          </div>

          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-2">
              {tags.map((tag) => (
                <span key={tag} className="font-bold text-xs">
                  # {tag.charAt(0).toUpperCase() + tag.slice(1)}
                </span>
              ))}
            </div>
          )}

          <div className="prose dark:prose-invert text-justify line-clamp-2">
            {description}
          </div>

          {coordinates && (
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              {coordinates}
            </div>
          )}
        </div>

        {imagePath && (
          <div
            className="w-24 h-24 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden flex-shrink-0 self-start sm:self-auto cursor-pointer"
            onClick={() => setIsImageOpen(true)}
          >
            <img
              src={imagePath}
              alt="Log visual"
              className="w-full h-full object-cover"
            />
          </div>
        )}
      </div>

      {isImageOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
          onClick={() => setIsImageOpen(false)}
        >
          <img
            src={imagePath}
            alt="Log visual"
            className="max-h-[90%] max-w-[90%] rounded-lg shadow-lg"
          />
        </div>
      )}
    </>
  );
}
